/**
 * Algoritmo de reparto de gastos.
 *
 * Soporta 4 modos:
 *   - equal:       partes iguales entre N participantes.
 *   - percentage:  cada participante indica su % (suman 100).
 *   - amount:      cada participante indica su importe absoluto (suman el total).
 *   - shares:      cada participante indica N partes (ej. 2 adultos = 2, 1 niño = 1).
 *
 * Soporta importes negativos (devoluciones): la lógica es simétrica, el signo se preserva.
 *
 * Residuo por redondeo: al pagador. Si hay múltiples pagadores, se elige uno
 * pseudo-aleatoriamente pero DETERMINISTA (semilla derivada del expense.id).
 */

import { type Money } from './money';
import type { CurrencyCode } from './currencies';

export type SplitMode = 'equal' | 'percentage' | 'amount' | 'shares';

export interface SplitParticipantInput {
  participant_id: string;
  /**
   * En modo 'equal': ignorado.
   * En modo 'percentage': porcentaje (0..100). Suma debe = 100.
   * En modo 'amount': importe absoluto en la unidad menor (bigint). Suma debe = |total|.
   * En modo 'shares': número de partes (positivo).
   */
  share_value?: number;
}

export interface SplitPayerInput {
  participant_id: string;
  /** Importe pagado por este pagador, en unidad menor (signed). */
  amount_paid_cents: bigint;
}

export interface SplitInput {
  expense_id: string;
  total: Money;
  mode: SplitMode;
  participants: SplitParticipantInput[];
  payers: SplitPayerInput[];
}

export interface SplitResult {
  participant_id: string;
  amount_owed_cents: bigint;
  share_value: number | null;
}

/**
 * Selecciona un pagador para asignarle el residuo de redondeo.
 * Determinista: la misma expense_id + set de pagadores siempre elige al mismo.
 */
export function chooseResidueTarget(
  expense_id: string,
  payer_ids: readonly string[]
): string {
  if (payer_ids.length === 0) throw new Error('No payers');
  if (payer_ids.length === 1) return payer_ids[0]!;

  // Hash simple determinista sobre expense_id
  let h = 0;
  for (let i = 0; i < expense_id.length; i++) {
    h = ((h << 5) - h + expense_id.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % payer_ids.length;
  return payer_ids[idx]!;
}

/**
 * Devuelve el reparto para cada participante.
 * Valida input; lanza si es inválido.
 * Garantiza que la suma de amount_owed_cents = total.cents.
 */
export function computeSplit(input: SplitInput): SplitResult[] {
  const { total, mode, participants, payers, expense_id } = input;

  if (participants.length === 0) throw new Error('At least one participant required');
  if (payers.length === 0) throw new Error('At least one payer required');
  if (total.cents === 0n) throw new Error('Total cannot be zero');

  // Verificar suma de pagadores = total
  const payersSum = payers.reduce((acc, p) => acc + p.amount_paid_cents, 0n);
  if (payersSum !== total.cents) {
    // Sin cifras (#56), igual que el `RAISE` del servidor. Se me pasó en el
    // primer intento —arreglé el de más abajo y di el fichero por hecho— y es
    // el que más se dispara de los dos: «los pagadores suman el total» es la
    // validación que más se incumple.
    throw new Error('Payers sum does not match total');
  }

  const sign = total.cents < 0n ? -1n : 1n;
  const absTotal = sign < 0n ? -total.cents : total.cents;
  const N = participants.length;

  const residueTargetId = chooseResidueTarget(
    expense_id,
    payers.map((p) => p.participant_id)
  );
  const residueIdx = participants.findIndex((p) => p.participant_id === residueTargetId);
  // Si el pagador elegido no es participante, el residuo va al primer participante
  const targetIdx = residueIdx >= 0 ? residueIdx : 0;

  let absShares: bigint[];
  let shareValues: (number | null)[];

  switch (mode) {
    case 'equal': {
      const base = absTotal / BigInt(N);
      const remainder = absTotal - base * BigInt(N);
      absShares = Array<bigint>(N).fill(base);
      absShares[targetIdx] = absShares[targetIdx]! + remainder;
      shareValues = Array<number | null>(N).fill(null);
      break;
    }

    case 'percentage': {
      const percents = participants.map((p) => {
        const v = p.share_value ?? 0;
        if (v < 0) throw new Error('Percentage cannot be negative');
        return v;
      });
      const sumPct = percents.reduce((a, b) => a + b, 0);
      if (Math.abs(sumPct - 100) > 0.0001) {
        throw new Error(`Percentages must sum to 100 (got ${sumPct})`);
      }
      // shares = absTotal * pct / 100 con redondeo half-even, ajuste residuo al pagador
      absShares = percents.map((pct) => {
        const num = BigInt(Math.round(pct * 1_000_000));
        // (absTotal * num) / 100_000_000 con redondeo half-even
        return roundHalfEvenBigInt(absTotal * num, 100_000_000n);
      });
      const sumShares = absShares.reduce((a, b) => a + b, 0n);
      const residue = absTotal - sumShares;
      absShares[targetIdx] = absShares[targetIdx]! + residue;
      shareValues = percents;
      break;
    }

    case 'amount': {
      const amounts = participants.map((p) => {
        const v = p.share_value ?? 0;
        if (v < 0) throw new Error('Amount cannot be negative in split');
        return BigInt(Math.round(v));
      });
      const sumAmounts = amounts.reduce((a, b) => a + b, 0n);
      if (sumAmounts !== absTotal) {
        // Sin los dos importes dentro (#56). Este `throw` sale de una mutación,
        // así que su mensaje acaba en `client_errors` y de ahí en el análisis
        // automático, donde la política promete que no llegan gastos de nadie.
        // Es el espejo exacto de `compute_split_cents`, que también los soltó:
        // arreglar sólo el servidor habría dejado la mitad del agujero abierta,
        // porque este camino se ejecuta antes de llamarlo.
        //
        // No se pierde nada: los dos números los acaba de escribir quien está
        // delante, y `computeSignature` ya los normalizaba fuera para agrupar.
        throw new Error('Amounts must sum to |total|');
      }
      absShares = amounts;
      shareValues = participants.map((p) => p.share_value ?? 0);
      break;
    }

    case 'shares': {
      const partsList = participants.map((p) => {
        const v = p.share_value ?? 1;
        if (v <= 0) throw new Error('Shares must be positive');
        return v;
      });
      const totalParts = partsList.reduce((a, b) => a + b, 0);
      if (totalParts <= 0) throw new Error('Total shares must be > 0');
      absShares = partsList.map((parts) => {
        const num = BigInt(Math.round(parts * 1_000_000));
        const den = BigInt(Math.round(totalParts * 1_000_000));
        return roundHalfEvenBigInt(absTotal * num, den);
      });
      const sumShares = absShares.reduce((a, b) => a + b, 0n);
      const residue = absTotal - sumShares;
      absShares[targetIdx] = absShares[targetIdx]! + residue;
      shareValues = partsList;
      break;
    }

    default: {
      // Exhaustive check
      const _exhaustive: never = mode;
      throw new Error(`Unknown split mode: ${_exhaustive as string}`);
    }
  }

  // Restaurar signo
  return participants.map((p, i) => ({
    participant_id: p.participant_id,
    amount_owed_cents: sign * absShares[i]!,
    share_value: shareValues[i] ?? null,
  }));
}

/**
 * Redondeo half-even sobre bigint: devuelve round(numerator / denominator)
 * usando banker's rounding (redondea al par en punto medio).
 */
function roundHalfEvenBigInt(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new Error('Division by zero');
  const q = numerator / denominator;
  const r = numerator - q * denominator;
  const twoR = r < 0n ? -r * 2n : r * 2n;
  const absDen = denominator < 0n ? -denominator : denominator;
  if (twoR < absDen) return q;
  if (twoR > absDen) return q + (numerator < 0n ? -1n : 1n);
  // Punto medio: redondear al par
  if (q % 2n === 0n) return q;
  return q + (numerator < 0n ? -1n : 1n);
}

/**
 * Valida que una descomposición de pagadores es consistente.
 * Uso previo a llamar a computeSplit.
 */
export function validatePayers(
  payers: SplitPayerInput[],
  total: Money
): { valid: boolean; message?: string } {
  if (payers.length === 0) return { valid: false, message: 'At least one payer required' };
  const seen = new Set<string>();
  for (const p of payers) {
    if (seen.has(p.participant_id)) {
      return { valid: false, message: `Duplicate payer: ${p.participant_id}` };
    }
    seen.add(p.participant_id);
    if (p.amount_paid_cents === 0n) {
      return { valid: false, message: 'Payer amount cannot be zero' };
    }
    if ((p.amount_paid_cents > 0n) !== (total.cents > 0n)) {
      return {
        valid: false,
        message: 'Payer amount must share sign with total',
      };
    }
  }
  const sum = payers.reduce((acc, p) => acc + p.amount_paid_cents, 0n);
  if (sum !== total.cents) {
    // Sin cifras (#56). Y éste es **el que de verdad se dispara**: en
    // `buildExpenseWriteArgs` `validatePayers` corre antes que `computeSplit`,
    // así que en el camino real de crear o editar un gasto tapa al de más
    // arriba. `api.ts` lo relanza como `ValidationError` dentro de la mutación,
    // o sea directo a `client_errors.message` y de ahí al análisis automático.
    //
    // Se me escapó en las dos primeras pasadas por mirar los `throw` y no los
    // `return { message }`: aquí el texto no se lanza, se devuelve, y acaba en
    // el mismo sitio.
    return { valid: false, message: 'Payer amounts do not sum to the total' };
  }
  return { valid: true };
}

/** Utilidad: reparto igualitario rápido (para pre-fill). */
export function equalSplitDefault(
  expense_id: string,
  total: Money,
  participant_ids: string[],
  payer_id: string,
  _currency: CurrencyCode
): SplitResult[] {
  return computeSplit({
    expense_id,
    total,
    mode: 'equal',
    participants: participant_ids.map((id) => ({ participant_id: id })),
    payers: [{ participant_id: payer_id, amount_paid_cents: total.cents }],
  });
}

/**
 * ¿Es coherente el reparto para el modo dado?
 *
 * Misma comprobación que hace `computeSplit` antes de lanzar, pero sin lanzar:
 * sirve para bloquear el botón de guardar y explicar el problema ANTES de
 * enviar, en vez de dejar que el usuario reciba el error interno.
 *
 * `shareValues` son los valores de los participantes INCLUIDOS.
 */
export function splitIsValid(
  mode: SplitMode,
  shareValues: readonly (number | null)[],
  absTotalCents: bigint
): boolean {
  if (shareValues.length === 0) return false;
  const sum = shareValues.reduce<number>((a, v) => a + (v ?? 0), 0);
  switch (mode) {
    case 'equal':
      return true;
    case 'percentage':
      return Math.abs(sum - 100) <= 0.0001;
    case 'amount':
      // computeSplit exige igualdad exacta al céntimo.
      return BigInt(Math.round(sum)) === absTotalCents;
    case 'shares':
      return shareValues.every((v) => (v ?? 1) > 0) && sum > 0;
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

/**
 * Reparte `totalCents` entre `n` huecos a partes iguales, **cuadrando al
 * céntimo**. El residuo va a los primeros huecos, uno a uno.
 *
 * El «al céntimo» no es un detalle de presentación: lo primero que se hace con
 * este reparto es comparar su suma con el total. Un `/ n` a secas deja 10,01
 * entre tres en 10,00 y el usuario ve un error de descuadre que no ha
 * provocado él.
 *
 * Conserva el signo, porque un gasto puede ser una devolución.
 */
export function splitCentsEvenly(totalCents: bigint, n: number): bigint[] {
  if (n <= 0) return [];
  const negative = totalCents < 0n;
  const absolute = negative ? -totalCents : totalCents;
  const slots = BigInt(n);
  const share = absolute / slots;
  const remainder = absolute - share * slots;
  return Array.from({ length: n }, (_, i) => {
    const v = share + (BigInt(i) < remainder ? 1n : 0n);
    return negative ? -v : v;
  });
}

/**
 * Reescala importes absolutos para que sumen exactamente `newAbsTotal`,
 * conservando las proporciones. El residuo del redondeo va al mayor, que es
 * donde menos se nota.
 *
 * Se usa al cambiar el importe total de un gasto que está en reparto absoluto:
 * sin esto, editar un gasto importado de 20 € a 21 € dejaba las partes sumando
 * 20 € y el guardado fallaba.
 */
export function rescaleAmountShares(
  shares: readonly number[],
  newAbsTotal: bigint
): number[] {
  const target = Number(newAbsTotal);
  const sum = shares.reduce((a, b) => a + b, 0);
  const n = shares.length;
  if (n === 0) return [];
  // Sin base sobre la que mantener proporción: reparto a partes iguales.
  if (sum <= 0) {
    const base = Math.floor(target / n);
    const out = Array<number>(n).fill(base);
    out[0] = base + (target - base * n);
    return out;
  }
  const out = shares.map((s) => Math.round((s * target) / sum));
  const residue = target - out.reduce((a, b) => a + b, 0);
  if (residue !== 0) {
    let idx = 0;
    for (let i = 1; i < n; i++) if (out[i]! > out[idx]!) idx = i;
    out[idx] = out[idx]! + residue;
  }
  return out;
}
